// QR Code Generation and Verification System
// Generates secure QR codes for order pickup verification

import { supabase } from './supabase'

export interface QRCodeData {
  orderId: string
  eventId: string
  userId: string
  expiresAt: string
  verificationToken: string
  type: 'pickup' | 'verification'
}

export interface QRCodeVerification {
  isValid: boolean
  order?: any
  event?: any
  error?: string
  alreadyUsed?: boolean
}

// Generate secure verification token
export const generateVerificationToken = async (orderId: string, eventId: string): Promise<string> => {
  const encoder = new TextEncoder()
  const data = encoder.encode(`${orderId}-${eventId}-${Date.now()}-${Math.random()}`)
  
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

// Generate QR code data for order pickup
export const generateOrderQRCode = async (orderId: string): Promise<QRCodeData> => {
  // Get order details
  const { data: order, error } = await supabase
    .from('secret_orders')
    .select(`
      *,
      secret_events (
        id,
        title,
        event_date,
        pickup_start_time,
        pickup_end_time
      )
    `)
    .eq('id', orderId)
    .single()

  if (error || !order) {
    throw new Error('Order not found')
  }

  // Generate verification token
  const verificationToken = await generateVerificationToken(orderId, order.event_id)
  
  // QR codes expire 30 minutes after pickup window ends
  const pickupEnd = new Date(`${order.secret_events.event_date} ${order.secret_events.pickup_end_time}`)
  const expiresAt = new Date(pickupEnd.getTime() + 30 * 60 * 1000)

  const qrData: QRCodeData = {
    orderId,
    eventId: order.event_id,
    userId: order.user_id,
    expiresAt: expiresAt.toISOString(),
    verificationToken,
    type: 'pickup'
  }

  // Store QR code in database for verification
  await supabase
    .from('secret_qr_codes')
    .upsert({
      order_id: orderId,
      verification_token: verificationToken,
      qr_data: JSON.stringify(qrData),
      expires_at: expiresAt.toISOString(),
      used_at: null,
      created_at: new Date().toISOString()
    })

  return qrData
}

// Generate QR code URL/string that can be scanned
export const generateQRCodeURL = (qrData: QRCodeData): string => {
  const baseURL = window.location.origin
  const params = new URLSearchParams({
    t: 'pickup',
    o: qrData.orderId,
    e: qrData.eventId,
    u: qrData.userId,
    exp: qrData.expiresAt,
    token: qrData.verificationToken
  })
  
  return `${baseURL}/verify?${params.toString()}`
}

// Verify QR code for pickup
export const verifyQRCode = async (qrParams: URLSearchParams): Promise<QRCodeVerification> => {
  const orderId = qrParams.get('o')
  const eventId = qrParams.get('e')
  const userId = qrParams.get('u')
  const expiresAt = qrParams.get('exp')
  const token = qrParams.get('token')
  const type = qrParams.get('t')

  if (!orderId || !eventId || !userId || !expiresAt || !token || type !== 'pickup') {
    return {
      isValid: false,
      error: 'Invalid QR code format'
    }
  }

  // Check if expired
  if (new Date() > new Date(expiresAt)) {
    return {
      isValid: false,
      error: 'QR code has expired'
    }
  }

  // Verify against database
  const { data: qrRecord, error: qrError } = await supabase
    .from('secret_qr_codes')
    .select('*')
    .eq('order_id', orderId)
    .eq('verification_token', token)
    .single()

  if (qrError || !qrRecord) {
    return {
      isValid: false,
      error: 'QR code not found or invalid'
    }
  }

  // Check if already used
  if (qrRecord.used_at) {
    return {
      isValid: false,
      alreadyUsed: true,
      error: 'QR code already used for pickup'
    }
  }

  // Get full order and event details
  const { data: order, error: orderError } = await supabase
    .from('secret_orders')
    .select(`
      *,
      secret_events (
        id,
        title,
        event_date,
        location_name,
        pickup_start_time,
        pickup_end_time
      ),
      secret_order_items (
        *,
        secret_menu_items (
          name,
          description,
          image_url
        )
      )
    `)
    .eq('id', orderId)
    .single()

  if (orderError || !order) {
    return {
      isValid: false,
      error: 'Order not found'
    }
  }

  // Verify order status is ready for pickup
  if (!['ready', 'completed'].includes(order.status)) {
    return {
      isValid: false,
      error: `Order not ready for pickup. Current status: ${order.status}`
    }
  }

  return {
    isValid: true,
    order,
    event: order.secret_events
  }
}

// Mark QR code as used and update order status
export const markQRCodeUsed = async (orderId: string, token: string): Promise<boolean> => {
  const now = new Date().toISOString()

  try {
    // Mark QR code as used
    const { error: qrError } = await supabase
      .from('secret_qr_codes')
      .update({ used_at: now })
      .eq('order_id', orderId)
      .eq('verification_token', token)

    if (qrError) {
      console.error('Failed to mark QR code as used:', qrError)
      return false
    }

    // Update order status to completed
    const { error: orderError } = await supabase
      .from('secret_orders')
      .update({ 
        status: 'completed',
        pickup_time: now,
        updated_at: now
      })
      .eq('id', orderId)

    if (orderError) {
      console.error('Failed to update order status:', orderError)
      return false
    }

    return true
  } catch (error) {
    console.error('Error marking QR code as used:', error)
    return false
  }
}

// Get all QR codes for an event (admin function)
export const getEventQRCodes = async (eventId: string) => {
  const { data, error } = await supabase
    .from('secret_qr_codes')
    .select(`
      *,
      secret_orders (
        order_number,
        total_amount,
        status,
        user_id,
        secret_users (
          first_name,
          last_name,
          email,
          phone
        )
      )
    `)
    .eq('secret_orders.event_id', eventId)
    .order('created_at', { ascending: false })

  if (error) {
    throw new Error(`Failed to fetch QR codes: ${error.message}`)
  }

  return data
}

// Clean up expired QR codes (maintenance function)
export const cleanupExpiredQRCodes = async (): Promise<number> => {
  const now = new Date().toISOString()
  
  const { data, error } = await supabase
    .from('secret_qr_codes')
    .delete()
    .lt('expires_at', now)
    .select('id')

  if (error) {
    console.error('Failed to cleanup expired QR codes:', error)
    return 0
  }

  return data?.length || 0
}